import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Flag, Check, AlertTriangle, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const FLAG_DEFINITIONS = [
  {
    key: 'multi_club_enabled',
    label: 'Multi-klubb medlemskap',
    description:
      'Lar medlemmer som er aktive i flere klubber bruke samme e-post/passord, med klubb-velger etter login. Aktiverer også tverr-klubb Politi-PDF.',
    risk: 'medium',
    risk_label: 'Middels risiko - test grundig i preview først',
  },
];

const FeatureFlagsPanel = () => {
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/feature-flags`);
        setFlags(res.data || {});
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleFlag = async (key, value) => {
    setSaving(key);
    setMessage(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API_BASE}/api/feature-flags`,
        { [key]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFlags(res.data || {});
      setMessage({ type: 'success', text: `${value ? 'Aktivert' : 'Deaktivert'} – endringen er live nå` });
      setTimeout(() => setMessage(null), 4000);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Kunne ikke oppdatere flag' });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-purple-500 flex items-center gap-3 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
        Laster feature flags...
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-purple-500 mb-6" data-testid="feature-flags-panel">
      <div className="flex items-start space-x-4">
        <Flag className="h-8 w-8 text-purple-400 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-2">Feature Flags</h3>
          <p className="text-gray-400 mb-4 text-sm">
            Aktiver eller deaktiver risikofyltefunksjoner på tvers av hele plattformen.
            Endringer trer i kraft umiddelbart for alle brukere.
          </p>

          {message && (
            <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
              message.type === 'success' ? 'bg-green-900/40 border border-green-700 text-green-200' : 'bg-red-900/40 border border-red-700 text-red-200'
            }`} data-testid="feature-flag-message">
              {message.type === 'success' ? <Check className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {message.text}
            </div>
          )}

          <div className="space-y-3">
            {FLAG_DEFINITIONS.map((def) => {
              const value = !!flags[def.key];
              const isSaving = saving === def.key;
              return (
                <div
                  key={def.key}
                  className="bg-gray-900/50 rounded-lg p-4 border border-gray-700"
                  data-testid={`feature-flag-row-${def.key}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium">{def.label}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                          def.risk === 'high' ? 'bg-red-500/20 text-red-300' :
                          def.risk === 'medium' ? 'bg-yellow-500/20 text-yellow-300' :
                          'bg-green-500/20 text-green-300'
                        }`}>
                          {def.risk === 'high' ? 'Høy risiko' : def.risk === 'medium' ? 'Middels' : 'Lav'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400">{def.description}</p>
                      <p className="text-xs text-gray-500 mt-1 italic">{def.risk_label}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={value}
                        onChange={(e) => toggleFlag(def.key, e.target.checked)}
                        disabled={isSaving}
                        data-testid={`feature-flag-toggle-${def.key}`}
                      />
                      <div className={`w-14 h-7 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-7 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-500 ${
                        isSaving ? 'opacity-50' : ''
                      }`}></div>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureFlagsPanel;
